import { FormEvent, KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { UseFormClearErrors, UseFormSetFocus, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ELoginAs, EOTPType, authenticationAPIController } from '@aqx-core/common/api/auth';
import { ECrmType } from '@aqx-core/common/api/user-account';
import { useRequestOtpTimer } from '@aqx-core/common/hooks/sign-up/use-request-otp-timer';
import { mapResponseCodeToMessage } from '@aqx-core/common/request';
import { IOtpLoginFormParams } from '@aqx-core/common/services/auth';
import { authenticationViewController } from '@aqx-core/common/services/auth/controller';
import { EUISnackBarMessageType, snackbarController } from '@aqx-core/common/services/snackbar';
import { getEnterKeycode } from '@aqx-core/common/utils/keyboard';
import { EMAIL_REGEX } from '@aqx-core/common/validators';

export const OTP_LENGTH = 6;

export enum EInputType {
  PHONE = 'PHONE',
  EMAIL = 'EMAIL',
  UNKNOWN = 'UNKNOWN'
}

export enum ELoginStep {
  STEP_1 = 'STEP_1',
  STEP_2_OTP = 'STEP_2_OTP',
  STEP_2_PASSWORD = 'STEP_2_PASSWORD'
}

// Helper to detect input type based on userId
const detectInputType = (userId: string): EInputType => {
  if (!userId || userId.length < 3) {
    return EInputType.UNKNOWN;
  }

  // Check if it matches email format
  if (EMAIL_REGEX.test(userId)) {
    return EInputType.EMAIL;
  }

  // Check if it contains ONLY digits (phone number)
  // If there are any non-digit characters, it's not a phone number
  if (/^\d+$/.test(userId)) {
    return EInputType.PHONE;
  }

  return EInputType.UNKNOWN;
};

// Helper to transfer values between fields when input type changes
const transferValuesBetweenFields = (
  previousType: EInputType,
  newType: EInputType,
  userId: string,
  setValue: UseFormSetValue<IOtpLoginFormParams>,
  phoneNumber?: string,
  dialCode?: string
) => {
  // Switching from UNKNOWN/EMAIL to PHONE: transfer userId to phoneNumber
  if (newType === EInputType.PHONE && previousType !== EInputType.PHONE && userId) {
    setValue('phoneNumber', userId);
    setValue('userId', '');
    // Set default country code to Korea +82 if no dial code is set
    if (!dialCode) {
      setValue('dialCode', '+82');
    }
  }
  // Switching from PHONE to UNKNOWN/EMAIL: transfer phoneNumber to userId
  else if (
    (newType === EInputType.UNKNOWN || newType === EInputType.EMAIL) &&
    previousType === EInputType.PHONE &&
    phoneNumber
  ) {
    setValue('userId', phoneNumber);
    setValue('phoneNumber', '');
    setValue('dialCode', ''); // Clear dial code when switching away from phone mode
  }
};

interface IUseOtpLoginStateProps {
  userId: string;
  dialCode?: string;
  phoneNumber?: string;
  reset: () => void;
  onTabChange: (value: ELoginAs) => void;
  setValue: UseFormSetValue<IOtpLoginFormParams>;
  setFocus: UseFormSetFocus<IOtpLoginFormParams>;
  clearErrors: UseFormClearErrors<IOtpLoginFormParams>;
  crmType?: ECrmType;
  prefilledUserId?: string;
  prefilledPassword?: string;
}

// eslint-disable-next-line max-lines-per-function -- Complex hook managing OTP login state and transitions
export const useOtpLoginState = ({
  userId,
  dialCode,
  phoneNumber,
  reset,
  onTabChange,
  setValue,
  setFocus,
  clearErrors,
  crmType = ECrmType.TRADAWISE,
  prefilledUserId,
  prefilledPassword
}: IUseOtpLoginStateProps) => {
  const [currentStep, setCurrentStep] = useState<ELoginStep>(ELoginStep.STEP_1);
  const [inputType, setInputType] = useState<EInputType>(EInputType.UNKNOWN);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [requestingOtp, setRequestingOtp] = useState(false);
  const { t } = useTranslation();

  const { startTimer, remainingTimeText, isResendDisabled } = useRequestOtpTimer({
    isSubmitting: requestingOtp,
    durationSeconds: 60
  });

  // Request OTP from server
  const requestOtp = useCallback(async () => {
    if (requestingOtp) {
      return;
    }

    setRequestingOtp(true);
    const otpType = inputType === EInputType.EMAIL ? EOTPType.EMAIL : EOTPType.SMS;
    const email = inputType === EInputType.EMAIL ? userId : undefined;
    const phone = inputType === EInputType.PHONE ? phoneNumber : undefined;
    const countryCode = inputType === EInputType.PHONE ? dialCode : undefined;

    try {
      await authenticationAPIController.requestCrmOtp(
        {
          crmType,
          type: otpType,
          email,
          phoneNumber: phone,
          countryCode
        },
        {
          onSuccess: () => {
            snackbarController.showAlert({
              message: t('Verification code has been sent successfully. Please check your email or SMS.'),
              messageType: EUISnackBarMessageType.SUCCESS
            });
            startTimer(); // Start the resend timer
          },
          onError: (error) => {
            const { code, message } = error;
            snackbarController.showAlert({
              message: mapResponseCodeToMessage(code, message),
              messageType: EUISnackBarMessageType.ERROR
            });
          }
        }
      );
    } finally {
      setRequestingOtp(false);
    }
  }, [userId, dialCode, phoneNumber, inputType, requestingOtp, crmType, startTimer, t]);

  // Detect input type based on userId value or phoneNumber
  const detectedInputType = useMemo(() => {
    // If phoneNumber field has value, check its content to determine type
    if (phoneNumber && phoneNumber.length > 0) {
      return detectInputType(phoneNumber);
    }
    // Otherwise check userId field
    return detectInputType(userId);
  }, [userId, phoneNumber]);

  // Handle input type change and transfer values between userId and phoneNumber
  useEffect(() => {
    const previousType = inputType;
    const newType = detectedInputType;

    if (previousType === newType) {
      return;
    }

    transferValuesBetweenFields(previousType, newType, userId, setValue, phoneNumber, dialCode);
    setInputType(newType);

    // Auto-focus phone number input when switching to PHONE mode
    if (newType === EInputType.PHONE) {
      // Use setTimeout to ensure the DOM has updated
      setTimeout(() => {
        setFocus('phoneNumber');
      }, 0);
    }
    // Auto-focus userId input when switching FROM phone to email/unknown mode
    else if (previousType === EInputType.PHONE && (newType === EInputType.EMAIL || newType === EInputType.UNKNOWN)) {
      setTimeout(() => {
        setFocus('userId');
      }, 0);
    }
  }, [detectedInputType, inputType, userId, phoneNumber, dialCode, setValue, setFocus]);

  const isStep1BtnDisabled = useMemo(() => {
    if (inputType === EInputType.PHONE) {
      return !dialCode || !phoneNumber || phoneNumber.length === 0;
    }
    return !userId || userId.length === 0 || inputType === EInputType.UNKNOWN;
  }, [userId, inputType, dialCode, phoneNumber]);

  const isOtpBtnDisabled = useMemo(() => {
    return otp.length !== OTP_LENGTH;
  }, [otp]);

  const handleStep1OtpLogin = useCallback(() => {
    // For phone mode, check phoneNumber instead of userId
    if (inputType === EInputType.PHONE) {
      if (!dialCode || !phoneNumber || phoneNumber.length === 0) {
        return;
      }
    } else {
      if (!userId || userId.length === 0 || inputType === EInputType.UNKNOWN) {
        return;
      }
    }
    setCurrentStep(ELoginStep.STEP_2_OTP);
    requestOtp();
  }, [userId, phoneNumber, dialCode, inputType, requestOtp]);

  const handleStep1PasswordLogin = useCallback(() => {
    if (!userId || userId.length === 0 || inputType !== EInputType.EMAIL) {
      return;
    }
    // Clear password field and validation for fresh start
    setValue('password', '');
    clearErrors('password');
    setCurrentStep(ELoginStep.STEP_2_PASSWORD);
  }, [userId, inputType, setValue, clearErrors]);

  const handleLoginWithPassword = useCallback(() => {
    // Clear password field and validation when switching from OTP to password
    setValue('password', '');
    clearErrors('password');
    setCurrentStep(ELoginStep.STEP_2_PASSWORD);
  }, [setValue, clearErrors]);

  const handleLoginWithOtp = useCallback(() => {
    // Clear OTP state when entering OTP screen
    setOtp('');
    setOtpError('');
    setCurrentStep(ELoginStep.STEP_2_OTP);
    requestOtp();
  }, [requestOtp]);

  const handleOtpChange = useCallback((pin: string) => {
    setOtp(pin);
    setOtpError('');
  }, []);

  const handleOtpError = useCallback((errorMessage: string) => {
    setOtpError(errorMessage);
    setOtp(''); // Clear the OTP input when there's an error
  }, []);

  const handleOtpSubmit = useCallback(() => {
    if (otp.length !== OTP_LENGTH) {
      setOtpError('Please input a valid 6-digit code');
      return false;
    }

    // Set the OTP value to the form
    setValue('password', otp);

    // For phone login, we need to set userId as the phone number with dial code
    if (inputType === EInputType.PHONE) {
      setValue('userId', `${dialCode}-${phoneNumber}`);
    }

    return true;
  }, [otp, setValue, inputType, dialCode, phoneNumber]);

  const handleResendOtp = useCallback(() => {
    requestOtp();
  }, [requestOtp]);

  const handleBackToStep1 = useCallback(() => {
    setCurrentStep(ELoginStep.STEP_1);
    // Clear OTP state
    setOtp('');
    setOtpError('');
    // Clear password field and validation
    setValue('password', '');
    clearErrors('password');
  }, [setValue, clearErrors]);

  const handleTabChange = useCallback(
    (nextValue: ELoginAs) => {
      reset();
      setCurrentStep(ELoginStep.STEP_1);
      setInputType(EInputType.UNKNOWN);
      // Clear OTP state
      setOtp('');
      setOtpError('');
      onTabChange(nextValue);
    },
    [reset, onTabChange]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!getEnterKeycode(event)) {
        return;
      }
      event.preventDefault();
      if (currentStep === ELoginStep.STEP_1) {
        handleStep1OtpLogin();
      }
      // For step 2, let the component handle the submission
    },
    [currentStep, handleStep1OtpLogin]
  );

  const handleStep1Submit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      handleStep1OtpLogin();
    },
    [handleStep1OtpLogin]
  );

  useEffect(() => {
    if (prefilledUserId) {
      setValue('userId', prefilledUserId);
      setValue('password', prefilledPassword ?? '');
      authenticationViewController.resetPrefilledValues();
    }
  }, [prefilledUserId, prefilledPassword, setValue]);

  return {
    currentStep,
    inputType,
    otp,
    otpError,
    isStep1BtnDisabled,
    isOtpBtnDisabled,
    handleStep1OtpLogin,
    handleStep1PasswordLogin,
    handleLoginWithPassword,
    handleLoginWithOtp,
    handleOtpChange,
    handleOtpSubmit,
    handleOtpError,
    handleResendOtp,
    handleBackToStep1,
    handleTabChange,
    handleKeyDown,
    handleStep1Submit,
    remainingTimeText,
    isResendDisabled
  };
};
